<script setup>
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { getI18n } from '@/plugins/i18n'
import { useDetectDevice } from '@/composables/device'
import { useDashboardStore } from '@/stores/dashboard.store'
import { useKpiMetricValue } from '@/composables/kpi-metric-value'

const props = defineProps({
  tileId: { type: String, default: '' },
  metricKey: { type: String, default: null },
  tileFilters: { type: Object, default: () => ({}) },
  tileName: { type: String, default: '' },
  warehouses: { type: Array, default: () => [] },
  // Static mode (on when `label` is set): render a supplied value/label, not a catalog metric.
  value: { type: [Number, String], default: null },
  label: { type: String, default: null },
  format: { type: Function, default: null },
})
const emit = defineEmits(['edit'])

const router = useRouter()
const { t } = getI18n()
const { isMobile } = useDetectDevice()
const dashboardStore = useDashboardStore()

const isStatic = computed(() => props.label !== null)
const selectedKey = computed(() => (isStatic.value ? null : (props.metricKey ?? dashboardStore.kpiDefaultByTile[props.tileId] ?? 'active_projects')))

const { value: metricValue, loading: metricLoading, metric } = useKpiMetricValue(() => selectedKey.value, () => props.tileFilters)

const displayLoading = computed(() => (isStatic.value ? false : metricLoading.value))
const displayValue = computed(() => {
  if (isStatic.value) return props.format ? props.format(props.value ?? 0) : String(props.value ?? 0)
  return metric.value ? metric.value.format(metricValue.value ?? 0) : ''
})
const displayLabel = computed(() => (isStatic.value ? props.label : (metric.value ? t(metric.value.labelKey) : '')))
</script>

<template>
  <div class="kpi-metric-tile fit" style="position: relative;">
    <q-btn
      v-if="isMobile && !isStatic"
      round
      flat
      dense
      size="xs"
      icon="mdi-cog-outline"
      color="grey-5"
      class="kpi-settings-btn"
      @mousedown.stop
      @click.stop="emit('edit')"
    />

    <div class="fit flex flex-center column text-center">
      <div v-if="displayLoading" class="col flex flex-center">
        <q-spinner size="md" color="primary" />
      </div>
      <template v-else>
        <div class="kpi-value text-weight-bold text-primary">
          {{ displayValue }}
        </div>
        <div class="text-caption text-grey-6 q-mt-xs">{{ displayLabel }}</div>
        <template v-if="!isStatic && metric">
          <div v-if="metric.additionalLabelKey" class="text-caption text-grey-5 q-mt-xs" style="font-style: italic;">
            {{ t(metric.additionalLabelKey) }}
          </div>
          <q-btn
            v-if="metric.link"
            flat
            dense
            no-caps
            size="sm"
            color="primary"
            icon-right="mdi-arrow-right"
            :label="t(metric.linkLabelKey)"
            class="q-mt-sm"
            @click="router.push(metric.link)"
          />
        </template>
      </template>
    </div>
  </div>
</template>

<style scoped>
.kpi-value {
  font-size: clamp(1.25rem, 12cqw, 3rem);
  line-height: 1.1;
}
.kpi-settings-btn {
  position: absolute;
  top: 4px;
  right: 4px;
  opacity: 0;
  transition: opacity 0.2s;
  z-index: 1;
}
.kpi-metric-tile:hover .kpi-settings-btn {
  opacity: 1;
}
</style>
